import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { all_routes } from '../../../router/all_routes';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showDeleteAlert, showDeleteConfirmation } from '../../../../core/alert/sweetAlertConfig';
import CreateParkingSlotModule from './CreateParkingSlotModule';
import ViewParkingSlotModule from './ViewParkingSlotModule';
import UpdateParkingSlotModule from './UpdateParkingSlotModule';

/* -------------------- Navigation items -------------------- */
const navItems = [
  { label: 'View Rooms', icon: 'ti ti-settings', link: all_routes.viewrooms, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Room Register', icon: 'ti ti-settings', link: all_routes.roomregister, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Location', icon: 'ti ti-plus', link: all_routes.location, permission: 'ROLE_ADD_LOCATION' },
  { label: 'View Parking Slots', icon: 'ti ti-settings', link: all_routes.viewparkingslots, permission: 'ROLE_GET_PARKING_SLOT' },
  { label: 'Parking Slot Register', icon: 'ti ti-settings', link: all_routes.parkingslotregister, permission: 'ROLE_GET_PARKING_SLOT' },
];

/* -------------------- DTOs -------------------- */
export interface LocationDTO {
  id: number; name: string; address: string; lat: number; lng: number;
  description?: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
export interface BuildingDTO {
  id: number; name: string; noOfFloors: number; locationName: string; locationId: number; description?: string | null;
}
export interface FloorDTO {
  id: number; name: string; floorNumber: number; buildingId: number; buildingName: string; locationName: string;
  createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface ParkingSlotDTO {
  id: number;
  slotName: string;
  x: string;
  y: string;
  locationId?: number | null;
  floorId?: number | null;
  locationName?: string | null;
  floorName?: string | null;
  status: string;
}
interface GetLocationsResponse { locations: LocationDTO[]; total: number; }
interface GetParkingSlotsResponse { parkingSlots: ParkingSlotDTO[]; total: number; }

/* -------------------- Helpers -------------------- */
const numericFirstSort = (a: string, b: string) => {
  const na = parseInt(a.replace(/\D+/g, ''), 10);
  const nb = parseInt(b.replace(/\D+/g, ''), 10);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const statusClass = (status?: string) => {
  const s = (status || '').toUpperCase();
  if (s === 'AVAILABLE') return 'bg-success-subtle text-success border-success-subtle';
  if (s === 'OCCUPIED') return 'bg-danger-subtle text-danger border-danger-subtle';
  if (s === 'RESERVED') return 'bg-primary-subtle text-primary border-primary-subtle';
  if (s === 'PERMANENTLY_ALLOCATED') return 'bg-secondary-subtle text-secondary border-secondary-subtle';
  return 'bg-secondary-subtle text-secondary border-secondary-subtle';
};

type ViewMode = 'table' | 'grid';

/* ==========================================================
   Component
========================================================== */
const ParkingSlotRegister: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();

  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null);

  const [floors, setFloors] = useState<FloorDTO[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);

  const [parkingSlots, setParkingSlots] = useState<ParkingSlotDTO[]>([]);
  const [selectedParkingSlot, setSelectedParkingSlot] = useState<ParkingSlotDTO | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UI controls
  const [query, setQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Modals
  const [showCreateParkingSlot, setShowCreateParkingSlot] = useState<boolean>(false);
  const [showViewParkingSlot, setShowViewParkingSlot] = useState<boolean>(false);
  const [showUpdateParkingSlot, setShowUpdateParkingSlot] = useState<boolean>(false);

  // -------- Name caches (to resolve IDs returned by slot APIs) ----------
  const [locCache, setLocCache] = useState<Record<number, string>>({});
  const [floorCache, setFloorCache] = useState<Record<number, string>>({});

  const upsertLocCache = (entries: Array<{ id: number; name: string }>) =>
    setLocCache(prev => ({ ...prev, ...Object.fromEntries(entries.map(e => [e.id, e.name])) }));

  const upsertFloorCache = (entries: Array<{ id: number; name: string }>) =>
    setFloorCache(prev => ({ ...prev, ...Object.fromEntries(entries.map(e => [e.id, e.name])) }));

  const getLocationName = (locationId?: number | null) => {
    if (!locationId) return null;
    return (
      locations.find(l => l.id === locationId)?.name ??
      locCache[locationId] ??
      null
    );
  };

  const getFloorName = (floorId?: number | null) => {
    if (!floorId) return null;
    return (
      floors.find(f => f.id === floorId)?.name ??
      floorCache[floorId] ??
      null
    );
  };

  // Resolve any missing location/floor names (fetch per-id once, then cache)
  const resolveMissingRefsForSlots = async (slots: ParkingSlotDTO[]) => {
    const missingLocIds = new Set<number>();
    const missingFloorIds = new Set<number>();

    for (const s of slots) {
      if (s.locationId && !getLocationName(s.locationId)) missingLocIds.add(s.locationId);
      if (s.floorId && !getFloorName(s.floorId)) missingFloorIds.add(s.floorId);
    }

    if (missingLocIds.size > 0) {
      const fetched: Array<{ id: number; name: string }> = [];
      await Promise.all(
        Array.from(missingLocIds).map(async (id) => {
          try {
            const res = await apiDefinitions.getLocationById(id);
            if (res.status === 200 && res.data?.data) {
              fetched.push({ id, name: res.data.data.name });
            }
          } catch { /* ignore */ }
        })
      );
      if (fetched.length) upsertLocCache(fetched);
    }

    if (missingFloorIds.size > 0) {
      const fetched: Array<{ id: number; name: string }> = [];
      await Promise.all(
        Array.from(missingFloorIds).map(async (id) => {
          try {
            const res = await apiDefinitions.getFloorById(id);
            if (res.status === 200 && res.data?.data) {
              fetched.push({ id, name: res.data.data.name });
            }
          } catch { /* ignore */ }
        })
      );
      if (fetched.length) upsertFloorCache(fetched);
    }
  };

  /* -------------------- Auth check -------------------- */
  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return token;
  };

  /* -------------------- Loads -------------------- */
  const fetchLocations = async () => {
    if (!checkToken()) return;
    setIsLoading(true);
    setError(null);
    try {
      const payload = { filterType: '', filterValue: '', date: '' };
      const response: AxiosResponse = await apiDefinitions.getLocations('1', payload);
      if (response.status === 200) {
        const data: GetLocationsResponse = response.data.data;
        setLocations(data.locations || []);
        if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch locations');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showError('Failed to fetch locations. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuildings = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_LOCATIONS')) {
      showError('You do not have permission to view buildings.');
      return;
    }
    if (!selectedLocationId) {
      setError('Please select a location');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId);
      if (response.status === 200) {
        const data: BuildingDTO[] = response.data.data;
        setBuildings(data || []);
        setSelectedBuildingId(null);
        setFloors([]);
        setSelectedFloorId(null);
        setParkingSlots([]);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No buildings found for this location');
      } else {
        showError('Failed to fetch buildings. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFloors = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_VIEW_FLOOR')) {
      showError('You do not have permission to view floors.');
      return;
    }
    if (!selectedBuildingId) {
      setError('Please select a building');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId);
      if (response.status === 200) {
        const data: FloorDTO[] = response.data.data;
        setFloors(data || []);
        setSelectedFloorId(null);
        setParkingSlots([]);
        if ((data || []).length === 0) setError('No floors found for the selected building.');
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('No floors found for this building');
      } else {
        showError('Failed to fetch floors. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchParkingSlots = async () => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_PARKING_SLOT')) {
      showError('You do not have permission to view parking slots.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let payload: { filterType: string; filterValue: string; date: string };
      if (selectedFloorId) {
        payload = { filterType: 'floorId', filterValue: String(selectedFloorId), date: '' };
      } else if (selectedBuildingId) {
        payload = { filterType: 'buildingId', filterValue: String(selectedBuildingId), date: '' };
      } else if (selectedLocationId) {
        payload = { filterType: 'locationId', filterValue: String(selectedLocationId), date: '' };
      } else {
        payload = { filterType: '', filterValue: '', date: '' }; // Fetch all
      }

      const response: AxiosResponse = await apiDefinitions.getAllParkingSlots('1', payload);
      if (response.status === 200) {
        const data: ParkingSlotDTO[] = (response.data?.data || []) as ParkingSlotDTO[];

        // Ensure we have names for the IDs that came back
        await resolveMissingRefsForSlots(data);

        const enhanced: ParkingSlotDTO[] = (data || [])
          .slice()
          .sort((a, b) => numericFirstSort(a.slotName, b.slotName))
          .map((slot: ParkingSlotDTO) => ({
            ...slot,
            locationName: slot.locationId
              ? (getLocationName(slot.locationId) ?? `Location #${slot.locationId}`)
              : (slot.locationName ?? null),
            floorName: slot.floorId
              ? (getFloorName(slot.floorId) ?? `Floor #${slot.floorId}`)
              : (slot.floorName ?? null),
          }));

        setParkingSlots(enhanced);

        if ((data || []).length === 0) {
          setError(
            selectedFloorId
              ? 'No parking slots found for the selected floor.'
              : selectedBuildingId
              ? 'No parking slots found for the selected building.'
              : selectedLocationId
              ? 'No parking slots found for the selected location.'
              : 'No parking slots found.'
          );
        }
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch parking slots');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 415) {
        showError('Unsupported request format. Please contact support.');
      } else if (err.response?.status === 404) {
        setError(
          selectedFloorId
            ? 'No parking slots found for this floor.'
            : selectedBuildingId
            ? 'No parking slots found for this building.'
            : selectedLocationId
            ? 'No parking slots found for this location.'
            : 'No parking slots found.'
        );
      } else {
        showError(
          `Failed to fetch parking slots${
            selectedFloorId ? ' for the selected floor'
            : selectedBuildingId ? ' for the selected building'
            : selectedLocationId ? ' for the selected location' : ''
          }. Please try again.`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- Slot actions -------------------- */
  const handleViewParkingSlot = async (slotId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_GET_PARKING_SLOT')) {
      showError('You do not have permission to view parking slot details.');
      return;
    }
    setIsLoading(true);
    try {
      const response: AxiosResponse = await apiDefinitions.getParkingSlotById(slotId);
      if (response.status === 200) {
        const slot: ParkingSlotDTO = response.data.data;
        // Enrich names via helpers/caches (fetch if absent)
        if (slot.locationId && !getLocationName(slot.locationId)) {
          try {
            const res = await apiDefinitions.getLocationById(slot.locationId);
            if (res.status === 200 && res.data?.data) upsertLocCache([{ id: slot.locationId, name: res.data.data.name }]);
          } catch { /* ignore */ }
        }
        if (slot.floorId && !getFloorName(slot.floorId)) {
          try {
            const res = await apiDefinitions.getFloorById(slot.floorId);
            if (res.status === 200 && res.data?.data) upsertFloorCache([{ id: slot.floorId, name: res.data.data.name }]);
          } catch { /* ignore */ }
        }
        slot.locationName = slot.locationId ? (getLocationName(slot.locationId) ?? slot.locationName ?? null) : (slot.locationName ?? null);
        slot.floorName = slot.floorId ? (getFloorName(slot.floorId) ?? slot.floorName ?? null) : (slot.floorName ?? null);

        setSelectedParkingSlot(slot);
        setShowViewParkingSlot(true);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch parking slot details');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        showError('Parking slot not found');
      } else {
        showError('Failed to fetch parking slot details. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditParkingSlot = async (slotId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_PARKING_SLOT')) {
      showError('You do not have permission to edit parking slots.');
      return;
    }
    setIsLoading(true);
    try {
      const response: AxiosResponse = await apiDefinitions.getParkingSlotById(slotId);
      if (response.status === 200) {
        const slot: ParkingSlotDTO = response.data.data;

        if (slot.locationId && !getLocationName(slot.locationId)) {
          try {
            const res = await apiDefinitions.getLocationById(slot.locationId);
            if (res.status === 200 && res.data?.data) upsertLocCache([{ id: slot.locationId, name: res.data.data.name }]);
          } catch { /* ignore */ }
        }
        if (slot.floorId && !getFloorName(slot.floorId)) {
          try {
            const res = await apiDefinitions.getFloorById(slot.floorId);
            if (res.status === 200 && res.data?.data) upsertFloorCache([{ id: slot.floorId, name: res.data.data.name }]);
          } catch { /* ignore */ }
        }
        slot.locationName = slot.locationId ? (getLocationName(slot.locationId) ?? slot.locationName ?? null) : (slot.locationName ?? null);
        slot.floorName = slot.floorId ? (getFloorName(slot.floorId) ?? slot.floorName ?? null) : (slot.floorName ?? null);

        setSelectedParkingSlot(slot);
        setShowUpdateParkingSlot(true);
      } else {
        showError(response.data?.statusDescription || 'Failed to fetch parking slot data');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else if (err.response?.status === 404) {
        showError('Parking slot not found');
      } else {
        showError('Failed to fetch parking slot data for editing. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteParkingSlot = async (slotId: number) => {
    if (!checkToken()) return;
    if (!checkPermission('ROLE_ADD_PARKING_SLOT')) {
      showError('You do not have permission to delete parking slots.');
      return;
    }
    showDeleteConfirmation(async () => {
      setIsLoading(true);
      try {
        const response: AxiosResponse = await apiDefinitions.deleteParkingSlot(slotId);
        if (response.status === 200) {
          showDeleteAlert("Parking slot deleted successfully");
          fetchParkingSlots();
        } else {
          showError(response.data?.statusDescription || 'Failed to delete parking slot');
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          showError('Unauthorized: Invalid or expired token. Please log in again.');
          localStorage.removeItem('token');
          navigate('/login');
        } else if (err.response?.status === 404) {
          showError('Parking slot not found');
        } else {
          showError('Failed to delete parking slot. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    });
  };

  /* -------------------- Effects -------------------- */
  useEffect(() => {
    const { state } = routerLocation as any;
    const initialize = async () => {
      await fetchLocations();

      // Restore (if navigated back with state)
      if (state?.refreshParkingSlots) {
        if (state.selectedLocationId) setSelectedLocationId(state.selectedLocationId);
        if (state.selectedBuildingId) setSelectedBuildingId(state.selectedBuildingId);
        if (state.selectedFloorId) setSelectedFloorId(state.selectedFloorId);
      }

      // Initial load
      await fetchParkingSlots();
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedLocationId) {
      setBuildings([]);
      setSelectedBuildingId(null);
      setFloors([]);
      setSelectedFloorId(null);
      setParkingSlots([]);
      fetchParkingSlots();
      return;
    }
    fetchBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setFloors([]);
      setSelectedFloorId(null);
      setParkingSlots([]);
      if (selectedLocationId) fetchParkingSlots();
      return;
    }
    fetchFloors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedFloorId || selectedBuildingId || selectedLocationId || (!selectedLocationId && !selectedBuildingId && !selectedFloorId)) {
      fetchParkingSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFloorId]);

  /* -------------------- UI helpers -------------------- */
  const openCreateParkingSlotModal = () => {
    if (!selectedLocationId && !selectedFloorId) {
      showError('Please select a location or floor to create a parking slot.');
      return;
    }
    setShowCreateParkingSlot(true);
  };

  const onCreateParkingSlotSuccess = () => {
    setShowCreateParkingSlot(false);
    fetchParkingSlots();
  };

  const onUpdateParkingSlotSuccess = () => {
    setShowUpdateParkingSlot(false);
    setSelectedParkingSlot(null);
    fetchParkingSlots();
  };

  const selectedLocationName = selectedLocationId ? (locations.find(l => l.id === selectedLocationId)?.name || '') : '';
  const selectedBuildingName = selectedBuildingId ? (buildings.find(b => b.id === selectedBuildingId)?.name || '') : '';
  const selectedFloorName = selectedFloorId ? (floors.find(f => f.id === selectedFloorId)?.name || '') : '';

  /* -------------------- Derived (search + status filter) -------------------- */
  const filteredSlots = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sset = new Set(statusFilters.map(s => s.toUpperCase()));
    const arr = (parkingSlots || []).filter((slot) => {
      if (q && !slot.slotName.toLowerCase().includes(q)) return false;
      if (sset.size && !sset.has((slot.status || '').toUpperCase())) return false;
      return true;
    });
    return arr.slice().sort((a, b) => numericFirstSort(a.slotName, b.slotName));
  }, [parkingSlots, query, statusFilters]);

  const STATUSES = [
    { key: 'AVAILABLE', label: 'Available' },
    { key: 'OCCUPIED', label: 'Occupied' },
    { key: 'RESERVED', label: 'Reserved' },
    { key: 'PERMANENTLY_ALLOCATED', label: 'Permanently Allocated' },
  ];
  const toggleStatus = (k: string) => {
    setStatusFilters((prev) => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
  };

  /* -------------------- Render -------------------- */
  return (
    <div className="page-wrapper">
      {/* Scoped UI styles */}
      <style>{`
        .slot-card { border-radius: .6rem; transition: transform .12s ease, box-shadow .12s ease; }
        .slot-card:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }
        .slot-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 60%; }
        .status-chip { cursor: pointer; user-select: none; }
        .view-toggle .btn { min-width: 84px; }
      `}</style>

      <div className="content">
        {/* Header */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8"><h4 className="page-title">Administration/Location/Parking Slot Register</h4></div>
                <div className="col-4 text-end"><div className="head-icons"><CollapseHeader /></div></div>
              </div>
            </div>

            {/* Tabs */}
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={index}>
                        <Link to={item.link} className={`nav-link px-0 ${index === 4 ? 'active' : ''}`}>
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div className="card">
          <div className="card-header justify-content-between">
            <div className="card-title">Parking Slot Register</div>
          </div>
          <div className="card-body">
            {/* Filters row */}
            <div className="row g-2 align-items-end">
              <div className="col-lg-3">
                <label className="form-label">Select Location</label>
                <select
                  className="form-select"
                  value={selectedLocationId || ''}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setSelectedLocationId(val);
                    setSelectedBuildingId(null);
                    setSelectedFloorId(null);
                  }}
                  disabled={isLoading}
                >
                  <option value="">{locations.length === 0 ? 'No locations available' : 'Select a location'}</option>
                  {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                </select>
              </div>

              <div className="col-lg-3">
                <label className="form-label">Select Building</label>
                <select
                  className="form-select"
                  value={selectedBuildingId || ''}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : null;
                    setSelectedBuildingId(val);
                    setSelectedFloorId(null);
                  }}
                  disabled={isLoading || !selectedLocationId}
                >
                  <option value="">{buildings.length === 0 ? 'No buildings available' : 'Select a building'}</option>
                  {buildings.map((bldg) => <option key={bldg.id} value={bldg.id}>{bldg.name}</option>)}
                </select>
              </div>

              <div className="col-lg-3">
                <label className="form-label">Select Floor (Optional)</label>
                <select
                  className="form-select"
                  value={selectedFloorId || ''}
                  onChange={(e) => setSelectedFloorId(e.target.value ? Number(e.target.value) : null)}
                  disabled={isLoading || !selectedBuildingId}
                >
                  <option value="">{floors.length === 0 ? 'No floors available' : 'Select a floor'}</option>
                  {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>

              <div className="col-lg-3 d-grid">
                <button
                  className="btn btn-success"
                  onClick={openCreateParkingSlotModal}
                  disabled={isLoading || (!selectedLocationId && !selectedFloorId) || !checkPermission('ROLE_ADD_PARKING_SLOT')}
                  title={!checkPermission('ROLE_ADD_PARKING_SLOT') ? 'You do not have permission to create parking slots' : undefined}
                >
                  Add Parking Slot
                </button>
              </div>
            </div>

            {/* Search + Status + View toggle */}
            <div className="row g-2 align-items-end mt-2">
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label">Search slots</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search by slot name…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-6">
                <label className="form-label">Status</label>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    { key: 'AVAILABLE', label: 'Available' },
                    { key: 'OCCUPIED', label: 'Occupied' },
                    { key: 'RESERVED', label: 'Reserved' },
                    { key: 'PERMANENTLY_ALLOCATED', label: 'Permanently' },
                  ].map(s => {
                    const active = statusFilters.includes(s.key);
                    return (
                      <span
                        key={s.key}
                        className={`badge rounded-pill px-3 py-2 status-chip ${active ? 'bg-primary text-white' : 'bg-light text-dark border'}`}
                        role="button"
                        onClick={() => setStatusFilters(prev => (prev.includes(s.key) ? prev.filter(x => x !== s.key) : [...prev, s.key]))}
                        title={s.label}
                      >
                        {s.label}
                      </span>
                    );
                  })}
                  {statusFilters.length > 0 && (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setStatusFilters([])}>Clear</button>
                  )}
                </div>
              </div>
              <div className="col-12 col-lg-3">
                <label className="form-label">View</label>
                <div className="btn-group w-100 view-toggle" role="group" aria-label="View toggle">
                  <button
                    className={`btn btn-outline-secondary ${viewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setViewMode('table')}
                  >
                    <i className="ti ti-table"></i> Table
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="ti ti-layout-grid"></i> Grid
                  </button>
                </div>
              </div>
            </div>

            {/* Loading Indicator */}
            {isLoading && (
              <div className="text-center my-3">
                <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
              </div>
            )}

            {/* Alerts */}
            {locations.length === 0 && (
              <div className="alert alert-info mt-3" role="alert">
                No locations available.{` `}
                {checkPermission('ROLE_ADD_LOCATION') && (
                  <Link to={all_routes.location} className="alert-link">Add a location</Link>
                )}
                .
              </div>
            )}

            {error && filteredSlots.length === 0 && locations.length > 0 && (
              <div className="alert alert-danger mt-3" role="alert">{error}</div>
            )}

            {/* Results */}
            {!isLoading && filteredSlots.length > 0 && (
              <>
                {viewMode === 'table' ? (
                  <div className="table-responsive mt-3">
                    <table className="table table-bordered align-middle">
                      <thead>
                        <tr>
                          <th style={{ minWidth: 160 }}>Slot Name</th>
                          <th style={{ minWidth: 160 }}>Location / Floor</th>
                          <th style={{ minWidth: 120 }}>Status</th>
                          <th style={{ minWidth: 160 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSlots.map((slot: ParkingSlotDTO) => (
                          <tr key={slot.id}>
                            <td>
                              <span className="fw-semibold">{slot.slotName}</span>
                             
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border me-1">{slot.floorName || slot.locationName || '-'}</span>
                            </td>
                            <td>
                              <span className={`badge border ${statusClass(slot.status)}`}>{slot.status}</span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-info me-1"
                                onClick={() => handleViewParkingSlot(slot.id)}
                                disabled={!checkPermission('ROLE_GET_PARKING_SLOT')}
                                title="View Parking Slot"
                              >
                                <i className="ti ti-eye"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-warning me-1"
                                onClick={() => handleEditParkingSlot(slot.id)}
                                disabled={!checkPermission('ROLE_ADD_PARKING_SLOT')}
                                title="Edit Parking Slot"
                              >
                                <i className="ti ti-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteParkingSlot(slot.id)}
                                disabled={!checkPermission('ROLE_ADD_PARKING_SLOT')}
                                title="Delete Parking Slot"
                              >
                                <i className="ti ti-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="row g-2 mt-3">
                    {filteredSlots.map((slot: ParkingSlotDTO) => (
                      <div key={slot.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <div className="card slot-card border">
                          <div className="card-body py-2 px-3 d-flex justify-content-between align-items-start">
                            <div className="me-2">
                              <div className="fw-semibold slot-name">{slot.slotName}</div>
                              <div className="text-muted small">{slot.floorName || slot.locationName || '-'}</div>
                              
                            </div>
                            <span className={`badge border ${statusClass(slot.status)}`}>{slot.status}</span>
                          </div>
                          <div className="card-footer py-2 d-flex gap-1 justify-content-end">
                            <button
                              className="btn btn-sm btn-light border"
                              onClick={() => handleViewParkingSlot(slot.id)}
                              disabled={!checkPermission('ROLE_GET_PARKING_SLOT')}
                              title="View"
                            >
                              <i className="ti ti-eye"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light border"
                              onClick={() => handleEditParkingSlot(slot.id)}
                              disabled={!checkPermission('ROLE_ADD_PARKING_SLOT')}
                              title="Edit"
                            >
                              <i className="ti ti-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light border"
                              onClick={() => handleDeleteParkingSlot(slot.id)}
                              disabled={!checkPermission('ROLE_ADD_PARKING_SLOT')}
                              title="Delete"
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Nothing found for current filters */}
            {!isLoading && locations.length > 0 && parkingSlots.length > 0 && filteredSlots.length === 0 && (
              <div className="alert alert-light border mt-3 d-flex align-items-center" role="alert">
                <i className="ti ti-info-circle me-2"></i>
                No parking slots match the current search or status filters.
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {showCreateParkingSlot && (selectedLocationId || selectedFloorId) && (
          <CreateParkingSlotModule
            show={showCreateParkingSlot}
            onHide={() => setShowCreateParkingSlot(false)}
            locationId={selectedFloorId ? null : selectedLocationId}
            floorId={selectedFloorId}
            locationName={selectedLocationName}
            buildingName={selectedBuildingName}
            floorName={selectedFloorName}
            onSuccess={onCreateParkingSlotSuccess}
          />
        )}

        {showViewParkingSlot && selectedParkingSlot && (
  <ViewParkingSlotModule
    show={showViewParkingSlot}
    onHide={() => {
      setShowViewParkingSlot(false);
      setSelectedParkingSlot(null);
    }}
    parkingSlot={{
      id: selectedParkingSlot.id,
      slotName: selectedParkingSlot.slotName,
      x: (selectedParkingSlot as any).x ?? null,         // your DTO uses string; API allows null
      y: (selectedParkingSlot as any).y ?? null,
      locationId: selectedParkingSlot.locationId ?? null,
      floorId: selectedParkingSlot.floorId ?? null,
      status: selectedParkingSlot.status,
      vehicleId: null,                 // not in DTO; fill as null
      visitorAllocations: null         // not in DTO; fill as null
    }}
  />
)}


        {showUpdateParkingSlot && selectedParkingSlot && (
          <UpdateParkingSlotModule
            show={showUpdateParkingSlot}
            onHide={() => {
              setShowUpdateParkingSlot(false);
              setSelectedParkingSlot(null);
            }}
            parkingSlot={selectedParkingSlot}
            locations={locations}
            buildings={buildings}
            floors={floors}
            onSuccess={onUpdateParkingSlotSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default ParkingSlotRegister;
